import type {
	commonBaseSystemField,
	GetOrganizationPermissionTemplatesResponsePermissionTemplate,
	OrganizationServiceGetOrganizationMemberListData,
	UpdateOrganizationRolesRequestPayload,
	v1GetOrganizationMemberListResponseMember,
	v1GetOrganizationRoleListResponse,
	v1OrganizationAppearanceConfig,
	v1OrgRole,
	v1WhiteLabelConfigs,
} from '@clients/api/organization';

import { size } from 'lodash';
import { action, makeAutoObservable, onBecomeObserved } from 'mobx';
import { GUEST_ROLE, OrganizationMemberStatusType } from './const';
import type { OrganizationStore } from './OrganizationStore';
import type { IOrganization } from './types';

export class Organization implements IOrganization {
	id?: string;
	name?: string;
	description?: string;
	baseSystemField?: commonBaseSystemField;
	enableWhiteLabel?: boolean;
	onlyOwnerInvite?: boolean;
	onlyOwnerInviteProjectMember?: boolean;
	capacity?: number;
	logoUrl?: string;
	projectIds?: string[];
	projectGroupIds?: string[];
	organizationMembers?: v1GetOrganizationMemberListResponseMember[];
	status?: string;
	enableMemberAccessToAllProjects?: boolean;
	disableAdminCreateNewProject?: boolean;
	purchaseOrderIds?: string[];
	whiteLabelConfigs?: v1WhiteLabelConfigs;
	bgUrl?: string;
	domain?: string;
	authDomain?: string;
	secretIds?: string[];
	canEdit?: boolean;
	orgRoles?: v1OrgRole[];
	enableIotHub?: boolean;
	enableCctvHub?: boolean;
	defaultEnableMfa?: boolean;
	mapConfig?: string;

	// additional properties
	appearance?: v1OrganizationAppearanceConfig;

	// Extend content
	store?: OrganizationStore;

	// External data
	orgRoleList?: v1OrgRole[];
	memberList: v1GetOrganizationMemberListResponseMember[] = [];
	totalMemberCount: number = 0;
	totalPendingMemberCount: number = 0;
	totalActiveMemberCount: number = 0;
	totalOrganizationMembersCount: number = 0;
	totalGuestCount: number = 0;
	// wait for backend
	totalTeamCount: number = 0;

	constructor(props?: Partial<Organization>) {
		makeAutoObservable(this);
		if (!props) return;

		// Initialize profile object
		Object.entries(props).forEach(([key, value]) => {
			(this as any)[key] = value;
		});

		onBecomeObserved(this, 'memberList', () => {
			void this.fetchMembers();
		});
		onBecomeObserved(this, 'totalMemberCount', () => {
			void this.fetchMembers();
		});
	}

	@action
	async fetchMembers(
		data?: Omit<OrganizationServiceGetOrganizationMemberListData, 'orgId'>
	) {
		try {
			const response = await this.store?.fetchMembers({
				orgId: this.id ?? '',
				...data,
			});

			if (response) {
				const { data, totalActive, totalCount, totalPending } = response;

				this.memberList = data ?? [];
				this.totalMemberCount = totalCount ?? 0;
				this.totalPendingMemberCount = totalPending ?? 0;
				this.totalActiveMemberCount = totalActive ?? 0;

				this.totalOrganizationMembersCount = size(
					this.memberList.filter(
						(member) =>
							member?.role?.name !== GUEST_ROLE &&
							member.status !== OrganizationMemberStatusType.REVOKED &&
							member.status !== OrganizationMemberStatusType.SUSPENDED
					)
				);

				this.totalGuestCount = size(
					this.memberList.filter(
						(member) =>
							member?.role?.name === GUEST_ROLE &&
							member.status !== OrganizationMemberStatusType.REVOKED &&
							member.status !== OrganizationMemberStatusType.SUSPENDED
					)
				);
			}

			return response;
		} catch (error) {
			console.log('🚀 ~ OrganizationStore ~ fetchMembers ~ error:', error);
		}
	}

	@action
	async fetchRoles(): Promise<v1GetOrganizationRoleListResponse> {
		const { roleList, templateOption } =
			(await this.store?.fetchOrganizationRoles(
				this.id!
			)) as v1GetOrganizationRoleListResponse;
		this.orgRoleList = roleList ?? [];

		return { roleList: roleList ?? [], templateOption };
	}

	@action
	async fetchPermissionTemplates(): Promise<
		GetOrganizationPermissionTemplatesResponsePermissionTemplate[]
	> {
		const result = await this.store?.fetchPermissionTemplatesByOrgId(this.id!);
		return result ?? [];
	}

	@action
	async updatePermissionRoles(
		body: UpdateOrganizationRolesRequestPayload[],
		templateOption?: string
	) {
		await this.store?.updateOrganizationPermissionRoles(this.id!, {
			payloads: body,
			templateOption,
		});
	}
}
